import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Divider,
  Modal,
  Progress,
  Radio,
  Typography,
  message,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import colors from '../../../../constants/ColourConstants';
import CustomButton from '../../../common/Button/Button';
import {
  regenerateStatus,
  startRegenerate,
} from '../../../../services/PayoutAPIs';
import { LOCK_MSG } from '../../../../constants/ApiConstants';
import { ActionContext } from '../../../common/Navigation/Navigation';

export default function RegenerateModal({
  open,
  setOpen,
  filterCount,
  filterFailed,
  selectedRows,
  filter,
  handlePayouts,
  handleExport,
  failedExportLoader,
  disabled,
}) {
  const { Text } = Typography;

  const [regenerateType, setRegenerateType] = useState('');
  const [count, setCount] = useState(0);

  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [percent, setPercent] = useState(0);
  const [regeneratedData, setRegeneratedData] = useState({});

  const [selectedFailed, setSelectedFailed] = useState(0);
  const [failedKeys, setFailedKeys] = useState([]);

  const { setAction } = useContext(ActionContext);

  useEffect(() => {
    if (selectedRows?.length > 0) {
      let count = 0;
      let keys = [];
      selectedRows.map((ele) => {
        if (ele.status === 'Payout Failure' && !ele.reattempted) {
          count += 1;
          keys.push(ele.payoutId);
        }
      });
      setSelectedFailed(count);
      setFailedKeys(keys);
    }
  }, [selectedRows]);

  const handleSelect = (v) => {
    let count = 0;
    switch (v) {
      case 'query':
        count = filterFailed;
        break;
      case 'selected':
        count = selectedFailed;
    }
    setRegenerateType(v);
    setCount(count);
  };

  const handleOk = () => {
    setConfirm(false);
    handleRegenrate();
  };

  async function pollStatus(taskId) {
    let status = 'Processing';

    while (status === 'Processing') {
      setPercent((prev) => (prev < 90 ? prev + 10 : prev));
      const data = await handleTaskStatus(taskId);
      status = data.status;

      if (status !== 'Completed') {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      } else if (status === 'Completed') {
        setPercent(100);
        const successLen = data?.successPayoutIds?.length;
        const failedLen = data?.failedPayoutIds?.length;
        setTimeout(() => {
          setRegeneratedData({ ...data, successLen, failedLen });
          setLoading(false);
          setSuccess(true);
          setPercent(0);
        }, 1000);
      }
    }
  }

  const handleRegenrate = () => {
    setAction(true);
    setLoading(true);
    let payload = {};
    if (regenerateType === 'query') {
      payload = { payoutFilter: filter, payoutIds: [] };
    } else if (regenerateType === 'selected') {
      payload = { payoutIds: failedKeys, payoutFilter: {} };
    }
    startRegenerate(payload)
      .then((res) => {
        if (res) {
          if (res === LOCK_MSG) {
            setLoading(false);
            message.error(
              'System is Locked for any updates,Please try after some time.',
            );
          } else {
            pollStatus(res);
            setPercent(10);
          }
        } else {
          setLoading(false);
          message.error('Failed to regenrate payouts');
        }
      })
      .catch(() => {
        setLoading(false);
        message.error('Failed to regenrate payouts');
      })
      .finally(() => {
        setAction(false);
      });
  };

  const handleTaskStatus = async (id) => {
    try {
      const res = await regenerateStatus(id);
      return res;
    } catch {
      setLoading(false);
      message.error('Failed to regenrate payouts status');
    }
  };

  return (
    <>
      <Modal
        title="Regenerate Failed Payouts"
        open={open}
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="back" onClick={() => setOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => {
              setConfirm(true);
              setOpen(false);
            }}
            disabled={count === 0 || disabled}
          >
            Regenerate
          </Button>,
        ]}
        centered
      >
        <Divider style={{ marginTop: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Text style={{ fontWeight: 600, fontSize: '14px' }}>Scope</Text>
        </div>
        <Radio.Group
          onChange={(e) => handleSelect(e.target.value)}
          value={regenerateType}
          style={{
            display: 'flex',
            flexDirection: 'column',
            margin: '12px 0',
            gap: 6,
          }}
        >
          <Radio
            value={'query'}
          >{`Filtered Results (${filterCount} rows - ${filterFailed} failed payouts)`}</Radio>
          <Radio
            value={'selected'}
          >{`Selected Rows  (${selectedRows?.length} rows - ${selectedFailed} failed payouts)`}</Radio>
        </Radio.Group>
      </Modal>

      <Modal
        title={
          <div>
            <ExclamationCircleOutlined
              style={{ color: colors.colorWarning, width: 21, height: 21 }}
            />{' '}
            Confirm Failed Payouts to Regenerate
          </div>
        }
        closable={false}
        open={confirm}
        width={'25%'}
        footer={[
          <Button key="back" onClick={() => setConfirm(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => {
              handleOk();
            }}
            disabled={disabled}
          >
            Confirm
          </Button>,
        ]}
        centered
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            margin: '0px 28px',
          }}
        >
          <Text>
            You’re about to regenerate <b>{`${count} failed payouts.`}</b>
          </Text>
          <Text>Do you want to proceed?</Text>
        </div>
      </Modal>

      <Modal
        title={
          <div>
            <div>Regenerating Failed Payouts...</div>
            <Divider style={{ margin: '8px 0px' }} />
          </div>
        }
        open={loading}
        closable={false}
        width={'30%'}
        footer={[]}
        centered
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <Text style={{ textAlign: 'center' }}>
            We’re working on regenerating <b>{`${count} failed payouts.`}</b>
          </Text>
          <div style={{ margin: '0px 67px' }}>
            <Progress percent={percent} strokeColor="#23979A" />
          </div>
          <Text style={{ textAlign: 'center' }}>This may take a moment.</Text>
        </div>
      </Modal>

      <Modal
        title={<div>Failed Payouts Regenerated</div>}
        open={success}
        onCancel={() => {
          handlePayouts();
          setSuccess(false);
        }}
        width={'30%'}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <CustomButton
              key="back"
              shape="round"
              variant="outlined"
              size="middle"
              onClick={() => {
                handlePayouts();
                setSuccess(false);
              }}
            >
              Return to Payouts
            </CustomButton>
            <CustomButton
              key="back"
              shape="round"
              size="middle"
              onClick={() => {
                handlePayouts();
                setSuccess(false);
              }}
            >
              View Regenerated Payouts
            </CustomButton>
          </div>
        }
        centered
      >
        <Divider style={{ marginTop: 0 }} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            margin: '0px 28px',
          }}
        >
          <CheckCircleOutlined
            style={{
              color: '#006D75',
              fontSize: '100px',
              justifyContent: 'center',
              margin: '20px 0px',
            }}
          />
          <Text
            style={{ fontSize: '20px', fontWeight: '400', textAlign: 'center' }}
          >
            Successfully regenerated failed payouts
          </Text>

          <div style={{ margin: '16px 0px' }}>
            <Text>Here’s a summary of the changes:</Text>
            <ul>
              <li>
                <Text>
                  Total Failed Payouts: <b>{count}</b>
                </Text>
              </li>
            </ul>
          </div>

          <div style={{ display: 'flex' }}>
            {regeneratedData?.successLen > 0 && (
              <Card
                style={{
                  borderRadius: '10px',
                  border: `1px solid #B7EB8F`,
                  margin: '0px 10px 10px 0px',
                }}
              >
                <Text style={{ color: colors.colorTextSecondary }}>
                  Payouts Successfully Regenerated
                </Text>
                <br />
                <CheckOutlined
                  style={{
                    fontSize: '20px',
                    color: '#52C41A',
                    marginRight: '5px',
                  }}
                />
                <Text
                  style={{
                    fontSize: '26px',
                    color: '#52C41A',
                    fontWeight: 600,
                  }}
                >
                  {regeneratedData?.successLen}
                </Text>
              </Card>
            )}
            {regeneratedData?.failedLen > 0 && (
              <Card
                style={{
                  borderRadius: '10px',
                  border: `1px solid #FFCCC7`,
                  marginBottom: '10px',
                }}
              >
                <Text style={{ color: colors.colorTextSecondary }}>
                  Payouts Failed to Regenerate
                </Text>
                <br />
                <CloseOutlined
                  style={{
                    fontSize: '20px',
                    color: '#FF4D4F',
                    marginRight: '5px',
                  }}
                />
                <Text
                  style={{
                    fontSize: '26px',
                    color: '#FF4D4F',
                    fontWeight: 600,
                  }}
                >
                  {regeneratedData?.failedLen}
                </Text>
                <CustomButton
                  variant="link"
                  loading={failedExportLoader}
                  onClick={() => handleExport(regeneratedData?.taskId)}
                  icon={<ExportOutlined />}
                >
                  Export CSV
                </CustomButton>
              </Card>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
